import React from "react";
import data from "../data/biodata.json";
import "./ai_chatbox.scss";

const profile = data?.details?.about_me || {};
const contact = data?.details?.contact_details || {};
const experience = (data?.details?.histories?.tabs || []).flatMap(
  (tab) => tab.details || []
);
const skillGroups = data?.details?.all_skills || [];
const projects = (data?.details?.project_details?.details || []).flatMap(
  (group) => group.project || []
);
const services = data?.details?.services || [];
const aiTools = data?.details?.ai_tools || [];

const formatList = (items) => {
  if (!items.length) return "";
  if (items.length === 1) return items[0];
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
};

const createMessage = (role, content) => ({
  id: `${role}-${Date.now()}-${Math.random()}`,
  role,
  content,
});

const localAnswer = (question) => {
  const asked = question.toLowerCase().trim();
  const name = profile.name || "Abdul Rahman";
  const topSkills = skillGroups.flatMap((group) =>
    (group.skills || []).map((skill) => skill.title)
  );
  const currentRole = experience[0];

  if (/^(hi|hello|hey|salam|assalam)/.test(asked)) {
    return `Hi! I’m ${name}’s portfolio assistant. Ask me about his background, skills, projects, experience, or how to contact him.`;
  }

  if (
    asked.includes("who are you") ||
    asked.includes("tell me about yourself") ||
    asked.includes("tell me about him") ||
    asked.includes("about you") ||
    asked.includes("about himself") ||
    asked.includes("about me") ||
    asked.includes("who is he") ||
    asked.includes("who is abdul")
  ) {
    return `${name} is a ${profile.title?.toLowerCase() || "full-stack developer"} based in ${profile.location || "Dubai, UAE"}. He has ${profile.metrics?.[0]?.value || "6+"} years of experience building responsive web and mobile products with React, Flutter, Laravel, NestJS, and Node.js. He blends practical engineering with a strong eye for product UX and is ${profile.availability?.toLowerCase() || "open to new opportunities"}.`;
  }

  if (
    asked.includes("location") ||
    asked.includes("where does") ||
    asked.includes("where is") ||
    asked.includes("based")
  ) {
    return `${name} is based in ${profile.location || "Dubai, United Arab Emirates"}.`;
  }

  if (
    asked.includes("role") ||
    asked.includes("job") ||
    asked.includes("title") ||
    asked.includes("what does he do") ||
    asked.includes("what do you do")
  ) {
    return `${name} is a ${profile.title || "Full Stack Developer"}. His work covers frontend architecture, full-stack applications, mobile apps, backend integrations, and polished UI implementation.`;
  }

  if (
    asked.includes("skill") ||
    asked.includes("technology") ||
    asked.includes("tech stack") ||
    asked.includes("framework") ||
    asked.includes("language") ||
    asked.includes("what can he")
  ) {
    return `His toolkit includes ${formatList(topSkills.slice(0, 16))}. He is especially focused on React and frontend architecture, Flutter apps, Laravel and API systems, responsive UI engineering, and AI-assisted delivery.`;
  }

  if (
    asked.includes("project") ||
    asked.includes("built") ||
    asked.includes("portfolio") ||
    asked.includes("work samples")
  ) {
    const highlights = projects
      .slice(0, 5)
      .map((project) => project.title)
      .filter(Boolean);
    return `Some projects in his portfolio include ${formatList(highlights)}. They span recruitment platforms, job portals, enterprise benchmarking tools, WhatsApp integrations, learning products, mobile apps, and e-commerce.`;
  }

  if (
    asked.includes("experience") ||
    asked.includes("career") ||
    asked.includes("worked") ||
    asked.includes("company")
  ) {
    const companies = [...new Set(experience.map((item) => item.company).filter(Boolean))];
    return `He has ${profile.metrics?.[0]?.value || "6+"} years of experience. He currently works as ${currentRole?.role || "a Frontend Developer"} at ${currentRole?.company || "Housekeeping Co"} in Dubai, and has also worked with ${formatList(companies.slice(1, 5))}. Earlier experience includes AR/VR design and Unity development.`;
  }

  if (
    asked.includes("service") ||
    asked.includes("offer") ||
    asked.includes("help with") ||
    asked.includes("hire")
  ) {
    return `He can help with ${formatList(services.map((service) => service.title).filter(Boolean))}. He is ${profile.availability?.toLowerCase() || "open to product and development opportunities"}.`;
  }

  if (
    asked.includes("ai") ||
    asked.includes("artificial intelligence") ||
    asked.includes("codex") ||
    asked.includes("chatgpt")
  ) {
    return `He uses AI as part of a practical engineering workflow. The AI tools listed in his portfolio include ${formatList(aiTools.map((tool) => tool.name).filter(Boolean))}, for coding, research, drafting, debugging, and experimentation.`;
  }

  if (
    asked.includes("hobby") ||
    asked.includes("interest") ||
    asked.includes("outside work") ||
    asked.includes("free time")
  ) {
    return "Outside work, he enjoys martial arts, photography, movies, comics, and exploring new technology tools. That creative curiosity shows up in the way he approaches product building.";
  }

  if (
    asked.includes("contact") ||
    asked.includes("email") ||
    asked.includes("reach") ||
    asked.includes("phone")
  ) {
    return `You can reach ${name} at ${contact.Email || "myselfabdul004@gmail.com"}. He is also available through the contact page and the social links in this portfolio.`;
  }

  if (asked.includes("resume") || asked.includes("cv")) {
    return "His resume is available from the Resume menu in the navigation. There are versions for Full Stack Developer and Frontend Developer roles.";
  }

  return `I can tell you about ${name}’s background, skills, projects, experience, services, AI workflow, interests, or contact details. Try asking “What does Abdul Rahman do?” or “What projects has he built?”`;
};

const askRemoteAssistant = async (question, history) => {
  if (typeof window === "undefined" || typeof fetch !== "function") {
    return null;
  }

  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 4500);

  try {
    const response = await fetch("/api/portfolio-chat", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      signal: controller.signal,
      body: JSON.stringify({
        message: question,
        history: history.slice(-6).map(({ role, content }) => ({ role, content })),
      }),
    });

    if (!response.ok) return null;
    const payload = await response.json();
    return payload.reply || null;
  } catch (error) {
    return null;
  } finally {
    window.clearTimeout(timeout);
  }
};

const AiChatbox = () => {
  const [open, setOpen] = React.useState(false);
  const [input, setInput] = React.useState("");
  const [isTyping, setIsTyping] = React.useState(false);
  const [messages, setMessages] = React.useState([
    createMessage(
      "assistant",
      `Hi, I’m Rahman AI. Ask me anything about ${profile.name || "Abdul Rahman"} — his work, skills, projects, or background.`
    ),
  ]);
  const messagesEndRef = React.useRef(null);
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  React.useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const sendMessage = async (question) => {
    const cleanQuestion = question.trim();
    if (!cleanQuestion || isTyping) return;

    setMessages((current) => [...current, createMessage("user", cleanQuestion)]);
    setInput("");
    setIsTyping(true);

    const remoteReply = await askRemoteAssistant(cleanQuestion, messages);
    const reply = remoteReply || localAnswer(cleanQuestion);
    setMessages((current) => [...current, createMessage("assistant", reply)]);
    setIsTyping(false);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    sendMessage(input);
  };

  return (
    <div className={`portfolio-chatbox${open ? " is-open" : ""}`}>
      {open ? (
        <section className="portfolio-chatbox__panel" aria-label="Rahman AI chat">
          <header className="portfolio-chatbox__header">
            <div className="portfolio-chatbox__identity">
              <div className="portfolio-chatbox__avatar" aria-hidden="true">
                AR
              </div>
              <div>
                <p className="portfolio-chatbox__name">Rahman AI</p>
                <p className="portfolio-chatbox__presence">
                  <span /> Portfolio concierge
                </p>
              </div>
            </div>
            <button
              type="button"
              className="portfolio-chatbox__close"
              onClick={() => setOpen(false)}
              aria-label="Close chat"
            >
              ×
            </button>
          </header>

          <div className="portfolio-chatbox__messages" aria-live="polite">
            <div className="portfolio-chatbox__context-note">
              <span>✦</span> Grounded in Abdul Rahman’s portfolio
            </div>
            {messages.map((message) => (
              <div
                className={`portfolio-chatbox__message portfolio-chatbox__message--${message.role}`}
                key={message.id}
              >
                {message.content}
              </div>
            ))}
            {isTyping ? (
              <div className="portfolio-chatbox__message portfolio-chatbox__message--assistant portfolio-chatbox__typing">
                <span />
                <span />
                <span />
              </div>
            ) : null}
            <div ref={messagesEndRef} />
          </div>

          <div className="portfolio-chatbox__suggestions">
            {["Who is Abdul Rahman?", "What are his skills?", "How can I contact him?"].map(
              (suggestion) => (
                <button
                  type="button"
                  key={suggestion}
                  onClick={() => sendMessage(suggestion)}
                  disabled={isTyping}
                >
                  {suggestion}
                </button>
              )
            )}
          </div>

          <form className="portfolio-chatbox__form" onSubmit={handleSubmit}>
            <label className="visually-hidden" htmlFor="portfolio-chatbox-input">
              Ask about Abdul Rahman
            </label>
            <input
              id="portfolio-chatbox-input"
              ref={inputRef}
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="Ask about Abdul Rahman..."
              maxLength={500}
              disabled={isTyping}
            />
            <button type="submit" aria-label="Send message" disabled={!input.trim() || isTyping}>
              <span aria-hidden="true">↗</span>
            </button>
          </form>
          <p className="portfolio-chatbox__disclaimer">
            Answers are based on the information shared in this portfolio.
          </p>
        </section>
      ) : null}

      <button
        type="button"
        className="portfolio-chatbox__launcher"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-label={open ? "Close Rahman AI" : "Ask Rahman AI about Abdul Rahman"}
      >
        <span className="portfolio-chatbox__launcher-icon" aria-hidden="true">
          {open ? "×" : "✦"}
        </span>
        {!open ? <span>Ask about Abdul</span> : null}
      </button>
    </div>
  );
};

export default AiChatbox;
